/**
 * REPLICAR UN PRESUPUESTO ANTERIOR EN LOS PASOS (REPLICAR-PRESUPUESTO-T1, Ares #43514). Sin DOM.
 *
 * Parte de un presupuesto guardado (GET /presupuestos/{id}) y arma uno NUEVO para el armador: sin codigo, version ni
 * cliente del anterior; cada seccion llevada a la seccion de la plantilla que le corresponde; cada partida con su
 * cantidad marcada "por confirmar", el precio de hoy del catalogo junto al que tenia y la diferencia. Lo que no
 * casa con ninguna seccion de la plantilla queda en "por clasificar", pero SIGUE contando en el total: el asesor
 * decide, la pantalla no borra nada en silencio.
 *
 * El tipo (item estandar) llega por `casamiento`: Map id de la partida (o productoId) -> {estandarId, nombre, tipo,
 * linea, confianza, sugerido}, armado con casamientoDesdeApi desde nexo-api 792. Por texto llega SUGERIDO, no elegido;
 * sin la ruta (aun no servida) la partida va sin tipo y el dia que la API lo mande se enciende solo.
 *
 * Las secciones e items salen con el vocabulario del armador (nombre, esAdicional, titulo, productoId, unidad,
 * cantidad, precioUnit), asi que calcularAiu de la vista las suma sin traduccion.
 *
 * @module application/presupuestos/replicarPresupuesto
 */

const de = (o, ...k) => { for (const c of k) if (o && o[c] !== undefined && o[c] !== null) return o[c]; return null; };
const lista = (v) => (Array.isArray(v) ? v : []);
const num = (v) => Number(v) || 0;

const SOBRAN = new Set(['de', 'del', 'y', 'e', 'en', 'la', 'el', 'las', 'los', 'para', 'con']);

/**
 * Las palabras de un nombre de seccion, para casarlo sin depender de como se escribio: sin tildes, sin el numero
 * del libro ("3. ESTRUCTURA DE PISO"), sin conectores y en singular. "Estructura Piso" y "3. ESTRUCTURA DE PISO"
 * dan lo mismo; "Cubierta" casa con CUBIERTAS y NO con ESTRUCTURA DE CUBIERTA (se compara el conjunto entero,
 * no un pedazo).
 */
export function palabrasDeSeccion(nombre) {
  const limpio = String(nombre ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/^\s*\d+\s*[.)-]?\s*/, '');
  const palabras = limpio.split(/[^a-z0-9]+/).filter((p) => p && !SOBRAN.has(p))
    .map((p) => (p.length > 3 && p.endsWith('s') ? p.slice(0, -1) : p));
  return [...new Set(palabras)].sort().join(' ');
}

/**
 * La seccion de la plantilla de una seccion del anterior: por su nombre o por cualquiera de sus nombresOrigen.
 * null si no casa con ninguna o si casa con mas de una (lo dudoso se clasifica a mano, no se adivina).
 */
export function seccionDePlantilla(nombre, seccionesPlantilla) {
  const buscada = palabrasDeSeccion(nombre);
  if (!buscada) return null;
  const casan = lista(seccionesPlantilla).filter((s) =>
    [s.nombre, s.clave, ...lista(s.nombresOrigen)].some((n) => palabrasDeSeccion(n) === buscada));
  const unicas = [...new Map(casan.map((s) => [s.clave || s.nombre, s])).values()];
  return unicas.length === 1 ? unicas[0] : null;
}

/**
 * El casamiento de A (nexo-api 792, GET /presupuestos/{id}/item-estandar): Map id de la partida -> su estandar.
 * Confianza producto o plantilla_item = el tipo llega elegido; texto = llega SUGERIDO (A: "es propuesta a confirmar",
 * #43550); por_clasificar o sin estandar = no entra. Acepta snake_case y camelCase.
 */
export function casamientoDesdeApi(data) {
  const m = new Map();
  for (const p of lista(de(data || {}, 'partidas', 'Partidas'))) {
    const id = de(p, 'item_id', 'itemId', 'ItemId');
    const e = de(p, 'estandar', 'Estandar');
    const confianza = String(de(p, 'confianza', 'Confianza') ?? '');
    if (!id || !e || confianza === 'por_clasificar') continue;
    m.set(String(id), {
      estandarId: de(e, 'id', 'Id'), nombre: de(e, 'nombre', 'Nombre'), tipo: de(e, 'tipo', 'Tipo'),
      linea: de(e, 'linea_nombre', 'lineaNombre', 'linea', 'Linea'), confianza, sugerido: confianza === 'texto'
    });
  }
  return m;
}

/** El precio de hoy de cada producto del catalogo (GET /productos): Map id -> precioVenta. */
export function preciosDeHoy(productos) {
  const m = new Map();
  for (const p of lista(productos)) {
    const id = de(p, 'id', 'Id');
    const v = de(p, 'precioVenta', 'precio_venta', 'PrecioVenta');
    if (id && v !== null && !Number.isNaN(Number(v))) m.set(String(id), Number(v));
  }
  return m;
}

/**
 * El presupuesto nuevo armado desde el anterior.
 *
 * @param {object} anterior  el GET /presupuestos/{id} (camelCase, PascalCase o snake_case)
 * @param {object} op
 *   seccionesPlantilla  secciones de normalizarPlantilla (con nombresOrigen)
 *   precios             Map productoId -> precio de hoy (preciosDeHoy)
 *   casamiento          Map productoId -> {linea, tipo, estandarId}, o null
 *   usar                'hoy' (por defecto) o 'anterior': que precio entra al armador
 * @returns {{origen, secciones, porClasificar, cuentas}}
 */
export function replicarPresupuesto(anterior, { seccionesPlantilla = [], precios = new Map(), casamiento = null, usar = 'hoy' } = {}) {
  const p = anterior || {};
  const r = de(p, 'resumen', 'Resumen') || {};
  const esAdic = (s) => !!de(s, 'esAdicional', 'es_adicional', 'EsAdicional');
  const orden = (s) => num(de(s, 'orden', 'Orden'));
  const secOrdenadas = [...lista(de(p, 'secciones', 'Secciones'))]
    .sort((a, b) => (esAdic(a) === esAdic(b) ? orden(a) - orden(b) : (esAdic(a) ? 1 : -1)));

  let partidas = 0; let conTipo = 0; let sugeridos = 0; let cambiaron = 0; let sinPrecioHoy = 0;
  const secciones = secOrdenadas.map((s) => {
    const nombreAnterior = String(de(s, 'nombre', 'Nombre') ?? 'Seccion');
    const destino = seccionDePlantilla(nombreAnterior, seccionesPlantilla);
    const items = lista(de(s, 'items', 'Items')).map((i) => {
      partidas++;
      const productoId = de(i, 'productoId', 'producto_id', 'ProductoId');
      const precioAnterior = num(de(i, 'precioUnit', 'precio_unit', 'PrecioUnit'));
      const hoy = productoId && precios.has(String(productoId)) ? precios.get(String(productoId)) : null;
      if (hoy === null) sinPrecioHoy++;
      else if (hoy !== precioAnterior) cambiaron++;
      const idPartida = de(i, 'id', 'Id');
      const estandar = (idPartida && casamiento?.get?.(String(idPartida))) || (productoId && casamiento?.get?.(String(productoId))) || null;
      if (estandar && estandar.sugerido) sugeridos++;
      else if (estandar) conTipo++;
      return {
        titulo: String(de(i, 'titulo', 'Titulo') ?? ''),
        productoId: productoId ? String(productoId) : null,
        unidad: de(i, 'unidad', 'Unidad') ?? '',
        cantidad: num(de(i, 'cantidad', 'Cantidad')),
        precioUnit: usar === 'anterior' || hoy === null ? precioAnterior : hoy,
        precioAnterior,
        precioHoy: hoy,
        diferencia: hoy === null ? null : hoy - precioAnterior,
        porConfirmar: true,
        estandar
      };
    });
    return {
      nombre: destino ? destino.nombre : nombreAnterior,
      nombreAnterior,
      clave: destino ? destino.clave : null,
      esAdicional: esAdic(s),
      porClasificar: !destino,
      items
    };
  });

  const porClasificar = secciones.filter((s) => s.porClasificar);
  return {
    origen: {
      id: de(p, 'id', 'Id'), codigo: de(p, 'codigo', 'Codigo'), version: de(p, 'version', 'Version'),
      utilidadPct: num(de(r, 'utilidadPct', 'UtilidadPct') ?? de(p, 'utilidadPct', 'utilidad_pct')),
      ivaUtilidadPct: num(de(r, 'ivaUtilidadPct', 'IvaUtilidadPct') ?? de(p, 'ivaUtilidadPct', 'iva_utilidad_pct')),
      descuento: num(de(r, 'descuento', 'Descuento') ?? de(p, 'descuento', 'Descuento')),
      areaM2: num(de(p, 'areaM2', 'area_m2', 'AreaM2'))
    },
    secciones,
    porClasificar,
    cuentas: {
      secciones: secciones.length, casadas: secciones.length - porClasificar.length,
      partidas, partidasPorClasificar: porClasificar.reduce((n, s) => n + s.items.length, 0),
      conTipo, sugeridos, preciosQueCambiaron: cambiaron, sinPrecioHoy
    }
  };
}

/** La frase de arriba del armador replicado: cuanto llego, cuanto falta y por que no hay tipo. */
export function fraseDeLaReplica(rep) {
  const c = rep?.cuentas;
  if (!c || !c.partidas) return 'El presupuesto anterior no trae partidas: no hay nada que replicar.';
  const cual = rep.origen?.codigo ? `${rep.origen.codigo}${rep.origen.version ? ' v' + rep.origen.version : ''}` : 'el anterior';
  const partes = [`Replicado de ${cual}: ${c.partidas} partidas en ${c.secciones} secciones, todas con la cantidad por confirmar.`];
  if (c.partidasPorClasificar) partes.push(`${c.partidasPorClasificar} quedan por clasificar (${c.secciones - c.casadas} secciones que no están en la plantilla); siguen sumando en el total.`);
  partes.push(c.preciosQueCambiaron ? `${c.preciosQueCambiaron} precios cambiaron desde entonces.` : 'Ningún precio cambió desde entonces.');
  if (c.sinPrecioHoy) partes.push(`${c.sinPrecioHoy} partidas no están en el catálogo de hoy y van con su precio anterior.`);
  if (c.conTipo) partes.push(`${c.conTipo} llegan con su tipo elegido.`);
  if (c.sugeridos) partes.push(`${c.sugeridos} traen un tipo sugerido por parecido del texto: confírmalo antes de mandar.`);
  if (!c.conTipo && !c.sugeridos) partes.push('El tipo no llega elegido: estas partidas todavía no están casadas con un ítem estándar.');
  return partes.join(' ');
}

/**
 * PR 4 (replicar sobre los pasos, Ares #43601): las partidas replicadas que tienen el tipo ELEGIDO (casamiento por
 * producto o por item de la plantilla) pasan a la seccion del cliente de su linea, con el item de la plantilla de su
 * estandar (asi el asistente las ve en su fila), su cantidad por confirmar y su precio de la replica (el total no se
 * mueve). Lo sugerido por texto y lo que no casa se queda en su seccion de antes, que el asistente muestra en el
 * Resumen como "por clasificar". Los adicionales no se mueven (su utilidad va en su propio bloque).
 * ubicar(estandarId) -> { n, nombre, item } | null.
 */
export function llevarReplicaALosPasos(rep, ubicar) {
  const destino = new Map();
  const restantes = [];
  for (const s of rep?.secciones || []) {
    const quedan = [];
    for (const it of s.items || []) {
      const u = !s.esAdicional && it.estandar && !it.estandar.sugerido && it.estandar.estandarId ? ubicar(it.estandar.estandarId) : null;
      if (!u || !u.item) { quedan.push(it); continue; }
      if (!destino.has(u.n)) destino.set(u.n, { nombre: u.nombre, seccionCliente: u.n, esAdicional: false, porClasificar: false, items: [] });
      destino.get(u.n).items.push({ ...it, plantillaItemId: u.item.id });
    }
    // Con otro nombre: si se llamara como una seccion del cliente, sumaria en ella sin verse en ninguna fila.
    if (quedan.length) restantes.push({ ...s, nombre: s.esAdicional ? s.nombre : `Por clasificar: ${s.nombreAnterior || s.nombre}`, items: quedan, porClasificar: !s.esAdicional });
  }
  const pasos = [...destino.values()].sort((a, b) => a.seccionCliente - b.seccionCliente);
  const obra = restantes.filter((s) => !s.esAdicional);
  const adicionales = restantes.filter((s) => s.esAdicional);
  return {
    secciones: [...pasos, ...obra, ...adicionales],
    enPasos: pasos.reduce((n, s) => n + s.items.length, 0),
    porClasificar: obra.reduce((n, s) => n + s.items.length, 0)
  };
}
